<template>
  <div class="consent-card-wrap">
    <main class="screen" role="main">

      <!-- View 1: path chooser -->
      <template v-if="!selectedPath">
        <p class="question-prompt">{{ $t('consent.chooser.prompt') }}</p>

        <div class="options">
          <button
            v-for="opt in pathOptions"
            :key="opt.value"
            type="button"
            class="option-label"
            @click="selectPath(opt.value)"
          >
            <span class="option-icon" aria-hidden="true">{{ opt.icon }}</span>
            <span class="option-content">
              <span class="option-title">{{ $t(`consent.chooser.${opt.value}.title`) }}</span>
              <span class="option-description">{{ $t(`consent.chooser.${opt.value}.description`) }}</span>
            </span>
          </button>
        </div>
      </template>

      <!-- View 2: consent panel for the chosen path -->
      <template v-else>
        <button type="button" class="change-selection" @click="changeSelection">
          ← {{ $t('consent.changeSelection') }}
        </button>

        <div class="intro">
          <p class="intro-eyebrow">{{ $t(`consent.paths.${selectedPath}.eyebrow`) }}</p>
          <h1 class="intro-title">{{ $t(`consent.paths.${selectedPath}.title`) }}</h1>
          <p class="intro-text">{{ $t(`consent.paths.${selectedPath}.intro`) }}</p>
        </div>

        <ul v-if="activeConfig.reassurance" class="info-list">
          <li v-for="key in reassuranceKeys" :key="key">
            <span class="info-icon" aria-hidden="true">✓</span>
            <span>{{ $t(`consent.paths.${selectedPath}.reassurance.${key}`) }}</span>
          </li>
        </ul>

        <label
          v-for="box in activeConfig.checkboxes"
          :key="box.key"
          class="consent-checkbox"
          :for="`consent-${box.key}`"
        >
          <input
            type="checkbox"
            :id="`consent-${box.key}`"
            v-model="affirmations[box.key]"
          >
          <span class="consent-text">
            {{ $t(`consent.paths.${selectedPath}.checkboxes.${box.key}`) }}
          </span>
        </label>
      </template>

    </main>

    <!-- Teacher path: coming-soon notice (Step 1 descope). Dismissing returns
         the card to the chooser exactly as shown on load. -->
    <div
      v-if="showTeacherNotice"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('consent.comingSoon.title')"
    >
      <div class="modal-dialog">
        <h2 class="modal-title">{{ $t('consent.comingSoon.title') }}</h2>
        <p class="modal-body">{{ $t('consent.comingSoon.body') }}</p>
        <button type="button" class="modal-button" @click="dismissTeacherNotice">
          {{ $t('consent.comingSoon.button') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n';

// Per-path panel composition. Structure lives here; wording lives in i18n
// under `consent.*`. Teacher is descoped for Step 1: it is offered in the
// chooser but opens a coming-soon notice instead of a panel, so it declares
// no fields/checkboxes and contributes no emitted state.
const PATH_CONFIG = {
  self: {
    icon: '👤',
    reassurance: true,
    fields: [],
    checkboxes: [{ key: 'participation', required: true }],
  },
  parent: {
    icon: '👨‍👧',
    reassurance: true,
    fields: [],
    checkboxes: [
      { key: 'participation', required: true },
      { key: 'research_data', required: true },
    ],
  },
  teacher: {
    icon: '🎓',
    comingSoon: true,
  },
};

const REASSURANCE_KEYS = ['r1', 'r2', 'r3', 'r4'];

export default {
  name: 'ConsentCard',
  props: {
    // The resolved junction item from the engine. Unused in Step 1 (content
    // is self-contained), accepted to honour the card/engine contract.
    item: { type: Object, default: null },
    // Prior state the engine previously stored for this card, or null.
    state: { type: Object, default: null },
  },
  emits: ['state-changed', 'request-advance'],
  data() {
    return {
      selectedPath: null,
      affirmations: {},
      showTeacherNotice: false,
      pathOptions: [
        { value: 'self', icon: PATH_CONFIG.self.icon },
        { value: 'parent', icon: PATH_CONFIG.parent.icon },
        { value: 'teacher', icon: PATH_CONFIG.teacher.icon },
      ],
    };
  },
  computed: {
    currentLocale() {
      return i18n.global.locale.value;
    },
    activeConfig() {
      return this.selectedPath ? PATH_CONFIG[this.selectedPath] : null;
    },
    reassuranceKeys() {
      return REASSURANCE_KEYS;
    },
    isValid() {
      const cfg = this.activeConfig;
      if (!cfg || cfg.comingSoon) return false;
      const fieldsOk = (cfg.fields || [])
        .filter((f) => f.required)
        .every((f) => String(this.affirmations[f.key] ?? '').trim().length > 0);
      const boxesOk = (cfg.checkboxes || [])
        .filter((c) => c.required)
        .every((c) => this.affirmations[c.key] === true);
      return fieldsOk && boxesOk;
    },
    // The state emitted to the engine on every input or locale change.
    // Full replacement, not a partial merge (per the card/engine contract).
    // Shape:
    //   {
    //     path: 'self' | 'parent' | null,   // null while on the chooser
    //     affirmations: { participation: true, research_data?: true },
    //     locale: 'en' | 'de' | 'fr' | 'it',
    //     valid: boolean                     // all required checkboxes ticked
    //   }
    // No timestamp here — the backend stamps that at persistence time (Step 2).
    // No `fields` for self/parent; the teacher path (which would add them) is
    // descoped for Step 1.
    emittedState() {
      const cfg = this.activeConfig;
      const affirmations = {};
      if (cfg && cfg.checkboxes) {
        cfg.checkboxes.forEach((c) => {
          affirmations[c.key] = this.affirmations[c.key] === true;
        });
      }
      return {
        path: this.selectedPath,
        affirmations,
        locale: this.currentLocale,
        valid: this.isValid,
      };
    },
  },
  watch: {
    emittedState: {
      handler(value) {
        this.$emit('state-changed', value);
      },
      immediate: true,
    },
  },
  created() {
    // Cards mount fresh and rehydrate from the state prop the engine passes in.
    const prior = this.state;
    if (prior && prior.path && PATH_CONFIG[prior.path] && !PATH_CONFIG[prior.path].comingSoon) {
      this.selectedPath = prior.path;
      this.affirmations = { ...(prior.affirmations || {}) };
    }
  },
  methods: {
    selectPath(path) {
      if (PATH_CONFIG[path].comingSoon) {
        this.showTeacherNotice = true;
        return;
      }
      this.selectedPath = path;
      this.affirmations = {};
    },
    changeSelection() {
      this.selectedPath = null;
      this.affirmations = {};
    },
    dismissTeacherNotice() {
      // Return to the chooser exactly as shown on load: no path selected,
      // no residual state.
      this.showTeacherNotice = false;
      this.selectedPath = null;
      this.affirmations = {};
    },
  },
};
</script>

<style scoped>
.consent-card-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.consent-card-wrap *,
.consent-card-wrap *::before,
.consent-card-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem 1.25rem;
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.question-prompt {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 1rem;
  color: var(--color-text-primary);
}

.options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-label {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 16px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  background: var(--color-bg-card);
  font-family: inherit;
  transition: background 0.15s, border-color 0.15s;
}

.option-label:hover {
  background: var(--color-bg-muted);
}

.option-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-bg-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
}

.option-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.option-title {
  font-size: 15px;
  font-weight: 500;
  margin: 0 0 4px;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.option-description {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.change-selection {
  align-self: flex-start;
  background: transparent;
  border: none;
  padding: 0 0 1.25rem;
  margin: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  color: var(--color-accent);
}

.change-selection:hover {
  text-decoration: underline;
}

.intro {
  margin-bottom: 1.5rem;
}

.intro-eyebrow {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 8px;
}

.intro-title {
  font-size: 24px;
  font-weight: 500;
  margin: 0 0 12px;
  line-height: 1.25;
}

.intro-text {
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.info-icon {
  flex-shrink: 0;
  color: var(--color-accent);
  font-weight: 600;
}

.consent-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  background: var(--color-bg-card);
  margin-bottom: 12px;
  transition: background 0.15s, border-color 0.15s;
}

.consent-checkbox:hover {
  background: var(--color-bg-muted);
}

.consent-checkbox:has(input:checked) {
  border-color: var(--color-accent);
  background: var(--color-accent-light);
}

.consent-checkbox input[type="checkbox"] {
  margin: 2px 0 0 0;
  width: 18px;
  height: 18px;
  accent-color: var(--color-accent);
  flex-shrink: 0;
}

.consent-text {
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.5;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 1000;
}

.modal-dialog {
  width: 100%;
  max-width: 360px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  padding: 1.5rem 1.25rem;
  text-align: center;
  font-family: var(--font-sans);
  color: var(--color-text-primary);
}

.modal-title {
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 12px;
}

.modal-body {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1.5rem;
  line-height: 1.6;
}

.modal-button {
  width: 100%;
  padding: 14px;
  background: var(--color-accent);
  color: white;
  border: 0.5px solid var(--color-accent);
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
}

.modal-button:hover {
  opacity: 0.9;
}
</style>
